import { useEffect, useRef } from "react";
import loggerClient from "@/libs/LoggerClient";

// "Agent" is already used elsewhere in this codebase for a governed
// data-access entity (AgentAttributeControllerService, ApplicationType.AI_AGENT).
// This hook is unrelated to that concept, hence "automation" naming throughout.
const STATIC_SIGNAL_SESSION_KEY = "tlx_automation_static_signal_reported";
const INTERACTION_ANOMALY_SESSION_KEY =
  "tlx_automation_interaction_anomaly_reported";

const INTERACTION_SAMPLE_SIZE = 12;
const ANOMALY_SCORE_THRESHOLD = 0.75;

type InteractionSample = {
  hadPrecedingMouseMove: boolean;
};

function detectStaticAutomationMarkers(): string[] {
  const markers: string[] = [];
  const nav = window.navigator as Navigator & { webdriver?: boolean };
  const win = window as unknown as Record<string, unknown>;
  const doc = document as unknown as Record<string, unknown>;

  if (nav.webdriver === true) {
    markers.push("navigator.webdriver");
  }
  if (Object.keys(window).some(key => key.startsWith("cdc_"))) {
    markers.push("window.cdc_*");
  }
  if (win.__playwright || win.__pw_manual) {
    markers.push("window.__playwright*");
  }
  if (win.__puppeteer_evaluation_script__) {
    markers.push("window.__puppeteer_evaluation_script__");
  }
  if (doc.$cdc_asdjflasutopfhvcZLmcfl_) {
    markers.push("document.$cdc_asdjflasutopfhvcZLmcfl_");
  }
  if (win.callPhantom || win._phantom) {
    markers.push("window.callPhantom/_phantom");
  }
  if (
    nav.plugins &&
    nav.plugins.length === 0 &&
    !/Mobi|Android/i.test(nav.userAgent)
  ) {
    markers.push("navigator.plugins.length===0");
  }
  if (!nav.languages || nav.languages.length === 0) {
    markers.push("navigator.languages.empty");
  }

  return markers;
}

// Headless Chrome/CDP sessions frequently fall back to a software WebGL
// renderer even when the reported user agent claims a normal desktop GPU.
function detectSoftwareRenderer(): string | null {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return null;
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    if (!debugInfo) return null;
    const renderer = gl.getParameter(
      debugInfo.UNMASKED_RENDERER_WEBGL,
    ) as string;
    if (/SwiftShader|llvmpipe|Software Rasterizer/i.test(renderer)) {
      return renderer;
    }
    return null;
  } catch {
    return null;
  }
}

// Best-effort, low-confidence detection of browser-automation-driven sessions
// (e.g. an AI/browser agent operating with a real user's valid login), for
// security review — not a security boundary. Reports at most one static-marker
// event and one interaction-anomaly event per browser session (sessionStorage
// guarded) via the existing AWS RUM telemetry sink (loggerClient).
const useAutomationDetection = (): void => {
  const samplesRef = useRef<InteractionSample[]>([]);
  const sawMouseMoveRef = useRef(false);
  const reportedInteractionRef = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem(STATIC_SIGNAL_SESSION_KEY)) return;

    const markers = detectStaticAutomationMarkers();
    const softwareRenderer = detectSoftwareRenderer();
    if (softwareRenderer) {
      markers.push(`webgl.renderer:${softwareRenderer}`);
    }

    if (markers.length > 0) {
      sessionStorage.setItem(STATIC_SIGNAL_SESSION_KEY, "1");
      loggerClient.recordEvent("Automation.StaticSignal", {
        markers,
        userAgent: window.navigator.userAgent,
      });
    }
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(INTERACTION_ANOMALY_SESSION_KEY)) return;

    const handleMouseMove = () => {
      sawMouseMoveRef.current = true;
    };

    // Samples whether each click/keypress was ever preceded by mouse movement.
    // A real human rarely produces a long run of clicks/keypresses with zero
    // mouse movement in between; a script dispatching events at coordinates
    // typically does.
    const recordSample = () => {
      if (reportedInteractionRef.current) return;

      samplesRef.current.push({
        hadPrecedingMouseMove: sawMouseMoveRef.current,
      });
      sawMouseMoveRef.current = false;

      if (samplesRef.current.length < INTERACTION_SAMPLE_SIZE) return;

      const scriptedLookingCount = samplesRef.current.filter(
        sample => !sample.hadPrecedingMouseMove,
      ).length;
      const score = scriptedLookingCount / samplesRef.current.length;

      if (score >= ANOMALY_SCORE_THRESHOLD) {
        reportedInteractionRef.current = true;
        sessionStorage.setItem(INTERACTION_ANOMALY_SESSION_KEY, "1");
        loggerClient.recordEvent("Automation.InteractionAnomaly", {
          score,
          sampleSize: samplesRef.current.length,
        });
      }

      samplesRef.current = [];
    };

    document.addEventListener("mousemove", handleMouseMove, {
      passive: true,
    });
    document.addEventListener("click", recordSample, { passive: true });
    document.addEventListener("keydown", recordSample, { passive: true });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("click", recordSample);
      document.removeEventListener("keydown", recordSample);
    };
  }, []);
};

export default useAutomationDetection;
